import { DomSanitizer, SafeStyle } from '@angular/platform-browser';
import {
  Component,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  ElementRef,
  AfterViewInit,
  OnChanges,
  ChangeDetectionStrategy,
  SimpleChanges
} from '@angular/core';
import { DEFAULT_FILTERS, ImageFilters } from '../image-filter.model';
import { environment } from '@env/environment';
import { HttpClient } from '@angular/common/http';

@Component({
  selector: 'app-image-editor',
  templateUrl: './image-editor.component.html',
  styleUrls: ['./image-editor.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ImageEditorComponent implements AfterViewInit, OnChanges {

  @Input() source: File | string;
  @Input() initialFilters: ImageFilters;
  @Input() compact: boolean = false;

  @Output() apply = new EventEmitter<{ blob: Blob; filters: ImageFilters }>();
  @Output() cancel = new EventEmitter<void>();

  @ViewChild('canvas') canvasRef: ElementRef<HTMLCanvasElement>;

  filters: ImageFilters = { ...DEFAULT_FILTERS };
  loading: boolean = false;
  error: string = '';
  imgWidth: number;
  imgHeight: number;
  resolvedBase64: string = '';
  previewUrl: string = '';
  cssFilter: SafeStyle = 'none';
  cssTransform: SafeStyle = 'none';
  outputWidth: number = 0;
  outputHeight: number = 0;

  private objectUrl: string | null = null;
  private readonly maxUploadBytes: number = 10400000;

  constructor(private sanitizer: DomSanitizer, private http: HttpClient) { }

  ngOnDestroy() {
    this.revokeObjectUrl();
  }

  ngAfterViewInit() {
    this.loadImage();
  }

  ngOnChanges(simpleChanges: SimpleChanges) {
    if (this.initialFilters) {
      this.filters = { ...this.initialFilters };
    }
    this.updateVisuals();
    if (simpleChanges.source) {
      this.loadImage();
    }
  }

  private draw(src: string) {
    this.loading = false;
    this.error = '';
    this.resolvedBase64 = src;
    // this.previewUrl = src;
    this.updateVisuals();
  }

  onImageLoaded() {
    this.loading = false;
    this.error = '';
  }

  onImageLoadFailed() {
    this.error = 'Unable to load image';
    this.resolvedBase64 = '';
    this.loading = false;
  }

  private base64ToBlob(base64: string): Blob | null {
    try {
      const parts = base64.split(',');
      const mime = parts[0].match(/data:(.*?);/)[1];
      const bstr = atob(parts[1]);
      let n = bstr.length;
      const u8arr = new Uint8Array(n);
      while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
      }
      return new Blob([u8arr], { type: mime });
    } catch {
      return null;
    }
  }

  private buildCssFilter(f: ImageFilters): string {
    const b = Math.max(0, f.brightness) + '%';
    const c = Math.max(0, f.contrast) + '%';
    const g = Math.max(0, f.grayscale) + '%';
    const i = Math.max(0, f.invert) + '%';
    const o = Math.max(0, f.opacity) + '%';
    const s = Math.max(0, f.saturate) + '%';
    const sp = Math.max(0, f.sepia) + '%';
    const bl = Math.max(0, f.blur) + 'px';
    const h = (f as any).hue != null ? (f as any).hue + 'deg' : '0deg';
    return `brightness(${b}) contrast(${c}) grayscale(${g}) invert(${i}) opacity(${o}) saturate(${s}) sepia(${sp}) blur(${bl}) hue-rotate(${h})`;
  }

  private updateVisuals() {
    this.cssFilter = this.sanitizer.bypassSecurityTrustStyle(this.buildCssFilter(this.filters));

    // Build Transform
    const f = this.filters;
    const rotate = f.rotate || 0;
    const scaleX = f.scaleX !== undefined ? f.scaleX : 1;
    const scaleY = f.scaleY !== undefined ? f.scaleY : 1;

    let translatePct = '';
    if (f.clipPath) {
      const tx = Math.max(0, f.right || 0) - Math.max(0, f.left || 0);
      const ty = Math.max(0, f.bottom || 0) - Math.max(0, f.top || 0);
      translatePct = `translate(${tx}%, ${ty}%) `;
    }

    // We combine rotate and scale. Note: Order matters.
    // CSS transform evaluates right-to-left: Scale -> Rotate -> Translate
    const transform = `${translatePct}rotate(${rotate}deg) scale(${scaleX}, ${scaleY})`;
    this.cssTransform = this.sanitizer.bypassSecurityTrustStyle(transform);
  }

  setFilter(key: keyof ImageFilters, value: number | boolean) {
    this.filters = { ...this.filters, [key]: value };
    this.updateVisuals();
  }

  setWidth(v: number) {
    this.outputWidth = Math.max(0, Math.round(v || 0));
  }

  setHeight(v: number) {
    this.outputHeight = Math.max(0, Math.round(v || 0));
  }

  async export() {
    // console.log('[DIAG-EXPORT] Apply clicked. resolvedBase64?', !!this.resolvedBase64, 'previewUrl?', !!this.previewUrl);
    // console.log('[DIAG-EXPORT] Current filters:', JSON.parse(JSON.stringify(this.filters)));

    if (this.resolvedBase64) {
      // console.log('[DIAG-EXPORT] BRANCH 1: Exporting from resolvedBase64');
      const blob = await this.renderImageConstrained(this.resolvedBase64, this.filters, this.maxUploadBytes);
      if (blob) {
        // console.log('[DIAG-EXPORT] BRANCH 1 SUCCESS: Blob size =', blob.size, 'type =', blob.type);
        this.apply.emit({ blob, filters: this.filters });
        return;
      }
      console.warn('[DIAG-EXPORT] BRANCH 1 FAILED: renderImageConstrained returned null');
    }

    if (this.previewUrl) {
      // console.log('[DIAG-EXPORT] BRANCH 2: Trying direct render from previewUrl:', this.previewUrl.substring(0, 80));
      let blob = await this.renderImageConstrained(this.previewUrl, this.filters, this.maxUploadBytes);
      if (blob) {
        // console.log('[DIAG-EXPORT] BRANCH 2 SUCCESS: Blob size =', blob.size, 'type =', blob.type);
        this.apply.emit({ blob, filters: this.filters });
        return;
      }
      console.warn('[DIAG-EXPORT] BRANCH 2 FAILED: direct render returned null (likely tainted canvas)');

      // console.log('[DIAG-EXPORT] BRANCH 3: Trying fetchToBase64 with cache bust...');
      let base64 = await this.fetchToBase64(this.previewUrl, true);

      if (!base64) {
        console.warn('[DIAG-EXPORT] BRANCH 3 FAILED: fetchToBase64 returned null');
        // console.log('[DIAG-EXPORT] BRANCH 4: Trying fetchViaProxy...');
        base64 = await this.fetchViaProxy(this.previewUrl);
      }

      if (base64) {
        // console.log('[DIAG-EXPORT] BRANCH', base64 ? '3 or 4' : '?', 'got base64, length =', base64.length, ', rendering...');
        blob = await this.renderImageConstrained(base64, this.filters, this.maxUploadBytes);
        if (blob) {
          // console.log('[DIAG-EXPORT] FINAL SUCCESS via fallback: Blob size =', blob.size, 'type =', blob.type);
          this.apply.emit({ blob, filters: this.filters });
          return;
        }
      }

      console.error('[DIAG-EXPORT] ALL BRANCHES EXHAUSTED - no blob produced');
      this.error = 'Export failed - Please ensure backend is running (localhost:3000) or deploy Proxy model to production.';
      return;
    }

    console.error('[DIAG-EXPORT] No image source at all');
    this.error = 'No image source available to export';
  }

  private async renderImageConstrained(src: string, f: ImageFilters, maxBytes: number): Promise<Blob | null> {
    return new Promise<Blob | null>(resolve => {
      const img = new Image();
      img.setAttribute('crossOrigin', 'anonymous');
      img.onload = async () => {
        const srcW = img.width;
        const srcH = img.height;
        let w = this.outputWidth || srcW;
        let h = this.outputHeight || srcH;
        const hasTransparency = (f.borderRadius && f.borderRadius > 0) ||
          (f.rotate && f.rotate % 90 !== 0) ||
          (f.opacity && f.opacity < 100);
        let preferredMime = hasTransparency ? 'image/png' : 'image/webp';
        let quality = 0.9;
        const minDim = 64;

        const renderOnce = async (mime: string, q?: number, fill?: string): Promise<Blob | null> => {
          const canvas = document.createElement('canvas');
          canvas.width = w;
          canvas.height = h;
          const ctx = canvas.getContext('2d');
          if (!ctx) return null;
          if (fill) {
            ctx.fillStyle = fill;
            ctx.fillRect(0, 0, w, h);
          }
          if (typeof (ctx as any).filter !== 'undefined') {
            (ctx as any).filter = this.buildCssFilter(f);
          }
          ctx.save();

          if (f.borderRadius && f.borderRadius > 0) {
            ctx.setTransform(1, 0, 0, 1, 0, 0);
            ctx.beginPath();
            const rr = Math.max(0, Math.min(f.borderRadius, Math.min(w, h) / 2));
            ctx.moveTo(rr, 0);
            ctx.arcTo(w, 0, w, rr, rr);
            ctx.arcTo(w, h, w - rr, h, rr);
            ctx.arcTo(0, h, 0, h - rr, rr);
            ctx.arcTo(0, 0, rr, 0, rr);
            ctx.closePath();
            ctx.clip();
          }

          let tx = 0;
          let ty = 0;
          if (f.clipPath) {
            tx = w * ((Math.max(0, f.right || 0) - Math.max(0, f.left || 0)) / 100);
            ty = h * ((Math.max(0, f.bottom || 0) - Math.max(0, f.top || 0)) / 100);
          }
          // console.log('[DIAG-CANVAS] ctx.translate values:', { tx, ty, totalX: w / 2 + tx, totalY: h / 2 + ty, w, h });
          // console.log('[DIAG-CANVAS] filters passed to render:', { clipPath: f.clipPath, top: f.top, bottom: f.bottom, left: f.left, right: f.right });
          ctx.translate(w / 2 + tx, h / 2 + ty);

          const rad = (f.rotate || 0) * Math.PI / 180;
          ctx.rotate(rad);
          const sx = f.scaleX || 1;
          const sy = f.scaleY || 1;
          ctx.scale(sx, sy);
          ctx.drawImage(img, -w / 2, -h / 2, w, h);
          ctx.restore();
          let blob: Blob | null = null;
          try {
            blob = await new Promise<Blob | null>((r, reject) => {
              if (canvas.toBlob) {
                try {
                  canvas.toBlob(b => {
                    // console.log('[DIAG-CANVAS] toBlob result:', b ? `OK (${b.size} bytes)` : 'NULL (tainted canvas?)');
                    r(b || null);
                  }, mime, q);
                } catch (toBlobErr) {
                  console.error('[DIAG-CANVAS] toBlob THREW (tainted canvas):', toBlobErr);
                  r(null);
                }
              } else {
                try {
                  const dataUrl = canvas.toDataURL(mime, q);
                  // console.log('[DIAG-CANVAS] toDataURL succeeded, length =', dataUrl.length);
                  r(this.base64ToBlob(dataUrl));
                } catch (dataUrlErr) {
                  console.error('[DIAG-CANVAS] toDataURL THREW (tainted canvas):', dataUrlErr);
                  r(null);
                }
              }
            });
          } catch (outerErr) {
            console.error('[DIAG-CANVAS] Outer blob creation error:', outerErr);
          }
          return blob;
        };

        let blob: Blob | null = await renderOnce(preferredMime, quality);
        if (!blob && !hasTransparency) {
          preferredMime = 'image/jpeg';
          blob = await renderOnce(preferredMime, quality);
        }
        if (!blob && hasTransparency) {
          preferredMime = 'image/png';
          blob = await renderOnce(preferredMime);
        }
        if (!blob) {
          resolve(null);
          return;
        }

        if (blob.size <= maxBytes) {
          resolve(blob);
          return;
        }

        if (!hasTransparency) {
          while (blob && blob.size > maxBytes && quality > 0.4) {
            quality = Math.max(0.4, quality - 0.1);
            blob = await renderOnce(preferredMime, quality);
          }
        }

        while (blob && blob.size > maxBytes && w > minDim && h > minDim) {
          w = Math.max(minDim, Math.round(w * 0.85));
          h = Math.max(minDim, Math.round(h * 0.85));
          blob = await renderOnce(preferredMime, !hasTransparency ? quality : undefined);
        }

        if (blob && blob.size > maxBytes && hasTransparency) {
          preferredMime = 'image/webp';
          blob = await renderOnce(preferredMime, 0.9);
        }

        if (blob && blob.size > maxBytes) {
          preferredMime = 'image/jpeg';
          quality = 0.9;
          let bg = '#ffffff';
          blob = await renderOnce(preferredMime, quality, bg);
          while (blob && blob.size > maxBytes && (quality > 0.4 || (w > minDim && h > minDim))) {
            if (quality > 0.4) {
              quality = Math.max(0.4, quality - 0.1);
            } else {
              w = Math.max(minDim, Math.round(w * 0.85));
              h = Math.max(minDim, Math.round(h * 0.85));
            }
            blob = await renderOnce(preferredMime, quality, bg);
          }
        }

        resolve(blob && blob.size <= maxBytes ? blob : null);
      };
      img.onerror = () => resolve(null);
      img.src = src;
    });
  }

  reset() {
    this.filters = this.initialFilters
      ? { ...DEFAULT_FILTERS, ...this.initialFilters }
      : { ...DEFAULT_FILTERS };
    this.outputWidth = 0;
    this.outputHeight = 0;
    this.updateVisuals();

    if (!this.resolvedBase64 && !this.previewUrl) {
      this.loadImage();
    }
  }

  private resolveSource(src: string): string {
    if (!src) return '';
    const v = String(((src as any) && ((src as any).changingThisBreaksApplicationSecurity || src)) || '').trim();
    if (/^(data:|https?:|blob:)/i.test(v)) return v;
    if (v.startsWith('/assets/')) return v;
    const base = String(environment.s3BucketUrl || '');
    return base + v;
  }

  private blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }
  private async fetchViaProxy(url: string): Promise<string | null> {
    const tryFetch = async (baseUrl: string) => {
      try {
        const proxyUrl = `${baseUrl}/api/Proxies/fetch-image?url=${encodeURIComponent(url)}`;
        // console.log('Fetching via proxy:', proxyUrl);
        return await this.http.get<any>(proxyUrl).toPromise();
      } catch (e) {
        throw e;
      }
    };

    try {
      // Try configured API URL first
      let response = await tryFetch(environment.apiBaseUrl).catch(async (e) => {
        // If 404 (endpoint missing on prod) and we are in dev mode (environment.production = false),
        // try localhost:3000 as a fallback for testing local backend changes.
        if (e.status === 404 && !environment.production) {
          console.warn('Proxy not found on main API, trying localhost:3000...');
          return await tryFetch('http://localhost:3000');
        }
        throw e;
      });

      if (response && response.base64) {
        if (response.base64.startsWith('data:')) {
          return response.base64;
        }
        const prefix = response.contentType ? `data:${response.contentType};base64,` : 'data:image/jpeg;base64,';
        return prefix + response.base64;
      }
      return null;
    } catch (e) {
      console.error('Proxy fetch failed:', e);
      return null;
    }
  }

  private async fetchToBase64(url: string, cacheBust: boolean = false): Promise<string | null> {
    try {
      const fetchUrl = cacheBust
        ? (url.includes('?') ? `${url}&t=${Date.now()}` : `${url}?t=${Date.now()}`)
        : url;

      const res = await fetch(fetchUrl, {
        cache: 'no-cache',
        credentials: 'omit'
      });

      if (!res.ok) return null;
      const blob = await res.blob();
      return await this.blobToBase64(blob);
    } catch (e) {
      console.warn('Image fetch failed:', e);
      return null;
    }
  }

  private async loadImage() {
    if (!this.source) return;

    this.loading = true;
    this.revokeObjectUrl();

    if (this.source instanceof File) {
      // For local files, converting to Base64 is safe
      try {
        const base64 = await this.blobToBase64(this.source);
        this.draw(base64);
      } catch (e) {
        this.error = 'Failed to read file';
        this.loading = false;
      }
    } else {
      const src = this.resolveSource(this.source as string);
      if (!src) {
        this.loading = false;
        return;
      }

      if (/^data:/i.test(src)) {
        this.draw(src);
      } else {
        // We use previewUrl for the fallback <img> tag
        this.previewUrl = src;

        // Try to fetch to get Base64
        let base64 = await this.fetchToBase64(src);
        if (!base64) {
          // Retry with cache busting
          base64 = await this.fetchToBase64(src, true);
        }

        if (base64) {
          this.draw(base64);
        } else {
          // Fallback: If fetch fails (likely CORS), we try to display via img tag (previewUrl)
          // but export might fail if we can't get data.
          // We will attempt proxy fetch on Export if needed.
          this.loading = false;
        }
      }
    }
  }

  private revokeObjectUrl() {
    if (this.objectUrl) {
      URL.revokeObjectURL(this.objectUrl);
      this.objectUrl = null;
    }
  }
}
